/**
 * Pure helpers for the Settings device "connection status" indicator.
 * Kept free of React/Firestore so the date arithmetic is unit-testable.
 *
 * `lastSeen` is the device's last heartbeat (epoch-ms, or null = never seen);
 * `now` is passed in so callers can tick the labels locally between server
 * polls without re-fetching.
 */

/** True iff the button checked in within the offline threshold. A null
 * lastSeen (never seen) is offline. Future timestamps (clock skew) count as
 * online — they're within threshold. */
export function isDeviceOnline(
  lastSeenMs: number | null,
  nowMs: number,
  thresholdMinutes: number,
): boolean {
  if (lastSeenMs === null) return false;
  return nowMs - lastSeenMs <= thresholdMinutes * 60_000;
}

/** Coarse "last checked in" label: "just now", "5 minutes ago", "1 hour
 * ago", "3 days ago", or "never". Coarse by design — the device heartbeats
 * every ~5 min, so sub-minute precision would be noise. */
export function formatLastSeen(lastSeenMs: number | null, nowMs: number): string {
  if (lastSeenMs === null) return 'never';
  const sec = Math.max(0, Math.round((nowMs - lastSeenMs) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const days = Math.floor(hr / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
